import { forwardRef, useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils.ts'

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode
  hint?: ReactNode
  wrapperClassName?: string
  /** Radix-style handler – called with the new boolean value when toggled. */
  onCheckedChange?: (checked: boolean) => void
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, hint, className, wrapperClassName, id, disabled, onCheckedChange, onChange, ...props }, ref) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onCheckedChange?.(e.target.checked)
    }
    const autoId = useId()
    const fieldId = id ?? autoId
    return (
      <label
        htmlFor={fieldId}
        className={cn(
          'inline-flex items-center gap-2 cursor-pointer select-none',
          disabled && 'cursor-not-allowed opacity-60',
          wrapperClassName,
        )}
      >
        <span className="relative inline-flex">
          <input
            ref={ref}
            id={fieldId}
            type="checkbox"
            role="switch"
            disabled={disabled}
            className={cn('peer sr-only', className)}
            onChange={handleChange}
            {...props}
          />
          <span
            className={cn(
              'h-5 w-9 rounded-full bg-gray-300 transition',
              'peer-checked:bg-brand-700',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/40',
            )}
          />
          <span
            aria-hidden
            className={cn(
              'absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
              'peer-checked:translate-x-4',
            )}
          />
        </span>
        {(label || hint) && (
          <span className="flex flex-col">
            {label ? <span className="text-body text-gray-900">{label}</span> : null}
            {hint ? <span className="text-caption text-gray-500">{hint}</span> : null}
          </span>
        )}
      </label>
    )
  },
)
Switch.displayName = 'Switch'
